/**
 * WeightSelectionModal - Modale de sélection de format pour produits au poids ou à la pièce
 * Permet au client de choisir entre :
 * - Par poids : 100g, 250g, 500g, 1kg ou un poids personnalisé (vrac)
 * - À la pièce : 1, 2, 3, 5 pièces ou un nombre personnalisé (si sell_by_pieces)
 */

import { useState } from 'react';
import type { Product } from '../../../types/ecommerce.types';
import { X } from 'lucide-react';

export interface QuantitySelection {
  weightGrams: number;
  quantityUnit: 'grams' | 'pieces';
  pieceCount?: number;
}

interface WeightSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  onConfirm: (selection: QuantitySelection) => void;
  /** Édition d'une ligne du panier : sélection actuelle, pré-remplie */
  initial?: QuantitySelection;
  /** Verbe du bouton de confirmation (« Ajouter » par défaut, « Modifier » depuis le panier) */
  confirmVerb?: string;
}

const WEIGHT_OPTIONS = [
  { grams: 100, label: '100g' },
  { grams: 250, label: '250g' },
  { grams: 500, label: '500g' },
  { grams: 1000, label: '1 kg' },
];

const PIECE_OPTIONS = [1, 2, 3, 5];

export default function WeightSelectionModal({
  isOpen,
  onClose,
  product,
  onConfirm,
  initial,
  confirmVerb = 'Ajouter',
}: WeightSelectionModalProps) {
  const canSellByPieces = product.sell_by_pieces && product.avg_weight_per_piece_grams;
  // « À la pièce uniquement » : l'onglet poids disparaît, mais JAMAIS au point de
  // ne laisser aucun mode — sans poids moyen par pièce, l'onglet pièce ne peut
  // pas s'afficher, donc le réglage est ignoré plutôt que de bloquer la vente.
  const canSellByWeight = product.is_weighted && !(product.ecom_pieces_only && canSellByPieces);
  const hasBothModes = canSellByPieces && canSellByWeight;

  // Onglet actif : 'weight' ou 'pieces'
  // Le POIDS passe devant quand les deux sont possibles (« 3 tomates cerises »
  // n'est pas une commande) ; la fiche peut demander la pièce d'abord pour ce
  // qui s'achète à l'unité (mangue, citron, poireau) — Philippe, 07/10/2026.
  const defaultTab = canSellByPieces && (!canSellByWeight || product.ecom_pieces_first) ? 'pieces' : 'weight';
  // Édition depuis le panier : on repart de ce que le client avait choisi
  // (le composant est monté avec une clé par ligne, l'état initial suffit).
  const initialPieces = initial?.quantityUnit === 'pieces' ? initial.pieceCount : undefined;
  const initialWeight = initial && initial.quantityUnit === 'grams' ? initial.weightGrams : undefined;
  const [activeTab, setActiveTab] = useState<'weight' | 'pieces'>(
    initialPieces && canSellByPieces ? 'pieces' : initialWeight ? 'weight' : defaultTab,
  );

  // État onglet poids
  const weightIsPreset = initialWeight !== undefined && WEIGHT_OPTIONS.some((o) => o.grams === initialWeight);
  const [selectedWeight, setSelectedWeight] = useState<number | 'custom'>(
    initialWeight === undefined ? 250 : weightIsPreset ? initialWeight : 'custom',
  );
  const [customWeight, setCustomWeight] = useState<string>(
    initialWeight !== undefined && !weightIsPreset ? String(initialWeight) : '',
  );

  // État onglet pièces
  const piecesIsPreset = initialPieces !== undefined && PIECE_OPTIONS.includes(initialPieces);
  const [selectedPieces, setSelectedPieces] = useState<number | 'custom'>(
    initialPieces === undefined ? 2 : piecesIsPreset ? initialPieces : 'custom',
  );
  const [customPieces, setCustomPieces] = useState<string>(
    initialPieces !== undefined && !piecesIsPreset ? String(initialPieces) : '',
  );

  if (!isOpen) return null;

  const priceEur = product.prices?.find((p) => p.currency === 'EUR');
  const pricePerKg = priceEur ? priceEur.amount_cents / 100 : 0;
  const avgWeightG = product.avg_weight_per_piece_grams || 200;

  // --- Calculs poids ---
  // Arrondi au centime comme le panier (Math.round sur des cents) : sur
  // 26,90 €/kg × 50 g, toFixed donnait 1,34 € et le panier 1,35 €.
  const priceCentsPerKg = priceEur ? priceEur.amount_cents : 0;
  const calculatePriceFromGrams = (weightGrams: number) => {
    return (Math.round((priceCentsPerKg * weightGrams) / 1000) / 100).toFixed(2);
  };

  const getEffectiveWeight = (): number => {
    if (selectedWeight === 'custom') {
      const parsed = parseInt(customWeight, 10);
      return isNaN(parsed) || parsed <= 0 ? 0 : parsed;
    }
    return selectedWeight;
  };

  const effectiveWeight = getEffectiveWeight();
  const isValidWeight = effectiveWeight >= 10;

  // --- Calculs pièces ---
  const getEffectivePieces = (): number => {
    if (selectedPieces === 'custom') {
      const parsed = parseInt(customPieces, 10);
      return isNaN(parsed) || parsed <= 0 ? 0 : parsed;
    }
    return selectedPieces;
  };

  const effectivePieces = getEffectivePieces();
  const isValidPieces = effectivePieces >= 1;
  const estimatedWeightForPieces = effectivePieces * avgWeightG;
  const priceForPieces = calculatePriceFromGrams(estimatedWeightForPieces);

  // --- Handlers ---
  const handleConfirm = () => {
    if (activeTab === 'weight' && isValidWeight) {
      onConfirm({ weightGrams: effectiveWeight, quantityUnit: 'grams' });
      onClose();
    } else if (activeTab === 'pieces' && isValidPieces) {
      onConfirm({
        weightGrams: estimatedWeightForPieces,
        quantityUnit: 'pieces',
        pieceCount: effectivePieces,
      });
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleCustomWeightChange = (value: string) => {
    setCustomWeight(value.replace(/\D/g, ''));
  };

  const handleCustomPiecesChange = (value: string) => {
    setCustomPieces(value.replace(/\D/g, ''));
  };

  const isValid = activeTab === 'weight' ? isValidWeight : isValidPieces;
  const confirmLabel = activeTab === 'weight'
    ? (isValidWeight ? `${confirmVerb} · ${calculatePriceFromGrams(effectiveWeight)} €` : '...')
    : (isValidPieces ? `${confirmVerb} · ${priceForPieces} €` : '...');

  return (
    <div
      onClick={handleOverlayClick}
      /* Échelle des plans, côté boutique :
           z-50  barre de navigation du bas
           z-60  modal produit
           z-70  ce qui s'ouvre DEPUIS la modal produit (poids)
           z-100 visionneuse d'images
         À égalité, c'est l'ordre du DOM qui tranche — et cette modal est rendue
         AVANT celle du produit, donc elle passerait dessous et deviendrait
         inutilisable : c'est arrivé le 26/08/2026 en montant la modal produit
         de 50 à 60 pour la faire passer devant la barre du bas. */
      className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-6"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative">
        {/* Bouton fermer */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full transition-all"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Contenu */}
        <div className="p-6">
          {/* En-tête */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-3xl">{activeTab === 'pieces' ? '🥕' : '⚖️'}</span>
              <h2 className="text-2xl font-bold text-gray-900">
                {hasBothModes ? 'Choisissez votre format' : activeTab === 'pieces' ? 'Combien de pièces ?' : 'Choisissez votre poids'}
              </h2>
            </div>
            <p className="text-gray-600 font-medium">{product.name}</p>
            <p className="text-sm text-gray-600 mt-1">
              Prix : {pricePerKg.toFixed(2)} €/kg
              {canSellByPieces && (
                <span className="ml-2 text-gray-400">
                  (~{((pricePerKg * avgWeightG) / 1000).toFixed(2)} €/pièce)
                </span>
              )}
            </p>
          </div>

          {/* Onglets poids / pièces (si les deux modes disponibles) */}
          {hasBothModes && (
            <div className="flex rounded-xl bg-gray-100 p-1 mb-5">
              <button
                onClick={() => setActiveTab('weight')}
                className={`flex-1 py-2.5 px-4 rounded-lg font-semibold text-sm transition-all ${
                  activeTab === 'weight'
                    ? 'bg-white text-green-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-700'
                }`}
              >
                ⚖️ Par poids
              </button>
              <button
                onClick={() => setActiveTab('pieces')}
                className={`flex-1 py-2.5 px-4 rounded-lg font-semibold text-sm transition-all ${
                  activeTab === 'pieces'
                    ? 'bg-white text-green-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-700'
                }`}
              >
                🥕 À la pièce
              </button>
            </div>
          )}

          {/* ═══ ONGLET POIDS ═══ */}
          {activeTab === 'weight' && (
            <>
              {/* Options de poids prédéfinies - Grille 2x2 */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {WEIGHT_OPTIONS.map((option) => {
                  const price = calculatePriceFromGrams(option.grams);
                  const isSelected = selectedWeight === option.grams;

                  return (
                    <button
                      key={option.grams}
                      onClick={() => setSelectedWeight(option.grams)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-bold text-gray-900 text-lg">{option.label}</div>
                        <div className="text-green-700 font-semibold">{price} €</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Option poids personnalisé (vrac) */}
              <div className="mb-5">
                <button
                  onClick={() => setSelectedWeight('custom')}
                  className={`w-full p-4 rounded-xl border-2 transition-all ${
                    selectedWeight === 'custom'
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedWeight === 'custom' ? 'border-green-600' : 'border-gray-300'
                        }`}
                      >
                        {selectedWeight === 'custom' && <div className="w-3 h-3 rounded-full bg-green-600" />}
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-gray-900">Poids personnalisé</div>
                        <div className="text-sm text-gray-600">Idéal pour le vrac</div>
                      </div>
                    </div>
                  </div>
                </button>

                {selectedWeight === 'custom' && (
                  <div className="mt-3 flex items-center gap-3">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={customWeight}
                      onChange={(e) => handleCustomWeightChange(e.target.value)}
                      placeholder="Ex: 350"
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl text-lg font-semibold text-center focus:outline-none focus:border-green-500"
                      autoFocus
                    />
                    <span className="text-lg font-medium text-gray-600">grammes</span>
                  </div>
                )}

                {selectedWeight === 'custom' && effectiveWeight > 0 && (
                  <div className="mt-2 text-center">
                    <span className="text-xl font-bold text-green-700">
                      {calculatePriceFromGrams(effectiveWeight)} €
                    </span>
                    <span className="text-gray-600 ml-2">
                      pour {effectiveWeight}g
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ═══ ONGLET PIÈCES ═══ */}
          {activeTab === 'pieces' && (
            <>
              {/* Options de pièces prédéfinies - Grille 2x2 */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {PIECE_OPTIONS.map((count) => {
                  const estWeight = count * avgWeightG;
                  const price = ((pricePerKg * estWeight) / 1000).toFixed(2);
                  const isSelected = selectedPieces === count;

                  return (
                    <button
                      key={count}
                      onClick={() => setSelectedPieces(count)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-bold text-gray-900 text-lg">
                          {count} pièce{count > 1 ? 's' : ''}
                        </div>
                        <div className="text-green-700 font-semibold">{price} €</div>
                        <div className="text-xs text-gray-400">~{estWeight}g</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Option nombre personnalisé */}
              <div className="mb-5">
                <button
                  onClick={() => setSelectedPieces('custom')}
                  className={`w-full p-4 rounded-xl border-2 transition-all ${
                    selectedPieces === 'custom'
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedPieces === 'custom' ? 'border-green-600' : 'border-gray-300'
                        }`}
                      >
                        {selectedPieces === 'custom' && <div className="w-3 h-3 rounded-full bg-green-600" />}
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-gray-900">Autre quantité</div>
                        <div className="text-sm text-gray-600">Choisir le nombre exact</div>
                      </div>
                    </div>
                  </div>
                </button>

                {selectedPieces === 'custom' && (
                  <div className="mt-3 flex items-center gap-3">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={customPieces}
                      onChange={(e) => handleCustomPiecesChange(e.target.value)}
                      placeholder="Ex: 4"
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl text-lg font-semibold text-center focus:outline-none focus:border-green-500"
                      autoFocus
                    />
                    <span className="text-lg font-medium text-gray-600">pièces</span>
                  </div>
                )}

                {selectedPieces === 'custom' && effectivePieces > 0 && (
                  <div className="mt-2 text-center">
                    <span className="text-xl font-bold text-green-700">
                      {priceForPieces} €
                    </span>
                    <span className="text-gray-600 ml-2">
                      {effectivePieces} pièce{effectivePieces > 1 ? 's' : ''} (~{estimatedWeightForPieces}g)
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Info */}
          <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              {activeTab === 'pieces'
                ? `Le poids est estimé à ~${avgWeightG}g par pièce. Le prix final sera ajusté au poids réel lors de la préparation.`
                : 'Le poids final sera ajusté par notre équipe lors de la préparation. Le prix sera recalculé en fonction du poids exact.'}
            </p>
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isValid}
              className={`flex-1 px-6 py-3 rounded-xl font-semibold shadow-lg transition-all active:scale-95 ${
                isValid
                  ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
                  : 'bg-gray-300 text-gray-600 cursor-not-allowed'
              }`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
